import { z } from "zod";
import { optional } from "./appointments";
import { recordId } from "./contracts";
import { searchTextUpTo, unset } from "./fields";

export const INVOICE_PAGE_SIZE_MAX = 1000;

const SEARCH_MAX_LENGTH = 200;

export const invoiceStatus = z.enum(["draft", "sent", "paid", "void"]);
export type InvoiceStatus = z.infer<typeof invoiceStatus>;

const named = z.object({ id: recordId, name: z.string() });

export const invoicePay = z.object({ subcontractor: z.number().nullable(), worker: z.number().nullable().optional() });
export type InvoicePay = z.infer<typeof invoicePay>;

export const appointmentBilled = z.object({
  kind: z.literal("appointment"),
  id: recordId,
  property: named.nullable(),
  unit: z.string().nullable(),
  service: z.object({ name: z.string(), category: z.string().nullable() }).nullable(),
  pay: invoicePay.optional(),
});
export type AppointmentBilled = z.infer<typeof appointmentBilled>;

export const contractPeriodBilled = z.object({
  kind: z.literal("contract"),
  id: recordId,
  property: named.nullable(),
  period: z.object({ start: z.string(), end: z.string() }),
});
export type ContractPeriodBilled = z.infer<typeof contractPeriodBilled>;

export const invoiceSubject = z.discriminatedUnion("kind", [appointmentBilled, contractPeriodBilled]);
export type InvoiceSubject = z.infer<typeof invoiceSubject>;

export const invoice = z.object({
  id: recordId,
  number: z.number().int(),
  status: invoiceStatus,
  vendor: named,
  customer: named,
  amount: z.number(),
  issuedAt: z.string(),
  dueAt: z.string(),
  paidAt: z.string().nullable(),
  billed: invoiceSubject,
});
export type Invoice = z.infer<typeof invoice>;

export const invoiceLineItem = z.object({
  id: recordId,
  item: z.string(),
  description: z.string().nullable(),
  quantity: z.number(),
  unitPrice: z.number(),
  total: z.number(),
});
export type InvoiceLineItem = z.infer<typeof invoiceLineItem>;

export const invoiceVendor = named.extend({
  phone: z.string().nullable(),
  location: z.string().nullable(),
  logo: z.string().nullable(),
});
export type InvoiceVendor = z.infer<typeof invoiceVendor>;

export const invoiceBillTo = z.object({
  name: z.string(),
  address: z.string().optional(),
});
export type InvoiceBillTo = z.infer<typeof invoiceBillTo>;

export const invoiceDetail = invoice.extend({
  vendor: invoiceVendor,
  billTo: invoiceBillTo,
  billingEmail: z.string().nullable(),
  worker: named.nullable(),
  lineItems: z.array(invoiceLineItem),
});
export type InvoiceDetail = z.infer<typeof invoiceDetail>;

export const invoiceReadQuery = z.object({
  actingOrganization: optional(recordId.toLowerCase()),
});
export type InvoiceReadQuery = z.infer<typeof invoiceReadQuery>;

export const invoiceSortKey = z.enum(["number", "issuedAt", "dueAt", "periodStart"]);
export type InvoiceSortKey = z.infer<typeof invoiceSortKey>;

const instant = z.string().datetime({ offset: true });

export const invoiceListQuery = invoiceReadQuery.extend({
  contract: optional(recordId.toLowerCase()),
  status: optional(invoiceStatus),
  search: z.preprocess((value) => (typeof value === "string" ? unset(value.trim()) : value), searchTextUpTo(SEARCH_MAX_LENGTH).optional()),
  issuedFrom: optional(instant),
  issuedTo: optional(instant),
  dueFrom: optional(instant),
  dueTo: optional(instant),
  numberFrom: optional(z.coerce.number().int().min(1).max(Number.MAX_SAFE_INTEGER)),
  sort: z.preprocess(unset, invoiceSortKey.default("number")),
  order: z.preprocess(unset, z.enum(["asc", "desc"]).default("desc")),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(INVOICE_PAGE_SIZE_MAX).default(25)),
});
export type InvoiceListQuery = z.infer<typeof invoiceListQuery>;

export const invoicePage = z.object({
  items: z.array(invoice),
  total: z.number().int().nonnegative(),
});
export type InvoicePage = z.infer<typeof invoicePage>;

export const invoiceStatusChange = z.enum(["sent", "paid", "void"]);
export type InvoiceStatusChange = z.infer<typeof invoiceStatusChange>;

export const invoiceStatusRequest = z.object({ status: invoiceStatusChange });
export type InvoiceStatusRequest = z.infer<typeof invoiceStatusRequest>;

export const invoiceBulkStatusRequest = z.object({
  invoices: z.array(recordId.toLowerCase()).min(1).max(INVOICE_PAGE_SIZE_MAX),
  status: invoiceStatusChange,
});
export type InvoiceBulkStatusRequest = z.infer<typeof invoiceBulkStatusRequest>;

export const invoiceEmailRequest = z.object({ email: z.string().trim().min(1).max(320).optional() });
export type InvoiceEmailRequest = z.infer<typeof invoiceEmailRequest>;

export const invoiceEmailQueued = z.object({ recipient: z.string(), alreadyQueued: z.boolean() });
export type InvoiceEmailQueued = z.infer<typeof invoiceEmailQueued>;
